import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Spinner, Alert, Form, Button, Card, Badge, Modal } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";

export default function ListedProductsListPage() {
    const { t } = useContext(TranslatorContext);

    // State Management
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [totalListedCount, setTotalListedCount] = useState(0);

    // Filter States
    const [search, setSearch] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Detail View Modal State
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [activeImage, setActiveImage] = useState("");

    // Dynamic Header Definitions
    const tableHeaders = [
        { name: "Product Code", width: "130px" },
        { name: "Image", width: "90px" },
        { name: "Title", width: "220px" },
        { name: "Category", width: "140px" },
        { name: "Brand", width: "120px" },
        { name: "Selling Style", width: "120px" },
        { name: "Price ($)", width: "120px" },
        { name: "Qty", width: "70px" },
        { name: "Action", width: "120px" }
    ];

    const fetchInitialData = useCallback(async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/lister/initial-data`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );
            if (response.data?.success) {
                setCategories(response.data?.data?.categories || response.data?.data?.product_categories || []);
            }
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    }, []);

    const fetchListedProducts = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {};
            if (search) params.search = search;
            if (categoryId) params.category_id = categoryId;
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/lister/listed-products`,
                {
                    params,
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            const resData = response.data;

            if (resData?.data) {
                setProducts(resData.data);
                setTotalListedCount(resData.total_listed_products || resData.data.length);
            } else {
                setProducts([]);
                setTotalListedCount(0);
            }
        } catch (err) {
            console.error("Error fetching listed products:", err);
            setError(
                err.response?.data?.message || "Failed to fetch listed products. Please try again."
            );
        } finally {
            setLoading(false);
        }
    }, [search, categoryId, fromDate, toDate]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    useEffect(() => {
        fetchListedProducts();
    }, [fetchListedProducts]);

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchListedProducts();
    };

    const handleResetFilters = () => {
        setSearch("");
        setCategoryId("");
        setFromDate("");
        setToDate("");
    };

    const handleOpenDetail = (product) => {
        setSelectedProduct(product);
        const mainImg = product.first_img || product.card_img || (product.images && product.images[0]?.image) || "";
        setActiveImage(mainImg);
        setShowDetailModal(true);
    };

    return (
        <PageLayout>
            <Row>
                {/* Header */}
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">
                                Listed Products ({totalListedCount})
                            </h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">
                                        {t("home") || "Home"}
                                    </Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">
                                        {t("products") || "Products"}
                                    </Link>
                                </li>
                                <li className="mc-breadcrumb-item">Listed Products</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Filters */}
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <Form onSubmit={handleFilterSubmit}>
                            <Row className="g-3">
                                <Col md={3} sm={6}>
                                    <Form.Group>
                                        <Form.Label>Search</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Code or Title..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3} sm={6}>
                                    <Form.Group>
                                        <Form.Label>Category</Form.Label>
                                        <Form.Select
                                            value={categoryId}
                                            onChange={(e) => setCategoryId(e.target.value)}
                                        >
                                            <option value="">All Categories</option>
                                            {categories.map((cat) => (
                                                <option key={cat.id} value={cat.id}>
                                                    {cat.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={2} sm={6}>
                                    <Form.Group>
                                        <Form.Label>From Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2} sm={6}>
                                    <Form.Group>
                                        <Form.Label>To Date</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2} sm={12} className="d-flex align-items-end gap-2">
                                    <Button type="submit" variant="primary" className="w-100">
                                        Filter
                                    </Button>
                                    <Button type="button" variant="secondary" className="w-100" onClick={handleResetFilters}>
                                        Reset
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </Col>

                {/* Main Table Container */}
                <Col xl={12}>
                    <div className="mc-card p-0" style={{ overflow: "hidden" }}>
                        {error && (
                            <div className="p-3">
                                <Alert variant="danger" className="m-0">
                                    {error}
                                </Alert>
                            </div>
                        )}

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">Loading listed products...</p>
                            </div>
                        ) : (
                            <div className="w-100" style={{ overflowX: "auto" }}>
                                <table
                                    className="table align-middle text-center table-hover mb-0"
                                    style={{ width: "100%", minWidth: "1050px", tableLayout: "fixed" }}
                                >
                                    <thead>
                                        <tr style={{ backgroundColor: "#2b77c0", color: "#ffffff" }}>
                                            {tableHeaders.map((header, index) => (
                                                <th
                                                    key={index}
                                                    style={{
                                                        width: header.width,
                                                        padding: "12px 8px",
                                                        fontSize: "13px",
                                                        fontWeight: "600",
                                                        textTransform: "uppercase",
                                                        color: "#ffffff",
                                                        border: "none"
                                                    }}
                                                >
                                                    {header.name}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products.length > 0 ? (
                                            products.map((item) => (
                                                <tr key={item.id} style={{ borderBottom: "1px solid #e9ecef" }}>
                                                    <td style={{ padding: "12px 8px" }}>
                                                        <span className="fw-bold text-primary text-truncate d-block">
                                                            {item.product_code}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: "12px 8px" }}>
                                                        <div
                                                            className="d-flex justify-content-center align-items-center rounded bg-light"
                                                            style={{ width: "45px", height: "45px", margin: "0 auto", overflow: "hidden" }}
                                                        >
                                                            <img
                                                                src={
                                                                    item.first_img
                                                                        ? `${process.env.REACT_APP_IMG_URL}${item.first_img}`
                                                                        : item.card_img
                                                                            ? `${process.env.REACT_APP_IMG_URL}${item.card_img}`
                                                                            : "/assets/images/placeholder.png"
                                                                }
                                                                alt={item.title}
                                                                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = "https://via.placeholder.com/45?text=No+Img";
                                                                }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="text-start" style={{ padding: "12px 8px" }}>
                                                        <div
                                                            className="text-truncate"
                                                            style={{ maxWidth: "100%" }}
                                                            title={item.title}
                                                        >
                                                            {item.title}
                                                        </div>
                                                    </td>
                                                    <td className="text-truncate" style={{ padding: "12px 8px" }}>
                                                        {item.category?.name || "N/A"}
                                                    </td>
                                                    <td className="text-truncate" style={{ padding: "12px 8px" }}>
                                                        {item.brand?.name || "N/A"}
                                                    </td>
                                                    <td style={{ padding: "12px 8px" }}>
                                                        <Badge bg={item.selling_style === "Fixed" ? "info" : "warning"}>
                                                            {item.selling_style}
                                                        </Badge>
                                                    </td>
                                                    <td className="fw-bold text-dark" style={{ padding: "12px 8px" }}>
                                                        ${item.sell_price_dollar ? Number(item.sell_price_dollar).toLocaleString() : "0"}
                                                    </td>
                                                    <td style={{ padding: "12px 8px" }}>{item.product_qty}</td>
                                                    <td style={{ padding: "12px 8px" }}>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="px-2 py-1 text-nowrap"
                                                            onClick={() => handleOpenDetail(item)}
                                                            style={{ fontSize: "12px" }}
                                                        >
                                                            VIEW DETAIL
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={tableHeaders.length} className="text-center py-4 text-muted">
                                                    No listed products found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Product Detail Modal */}
            {selectedProduct && (
                <Modal
                    show={showDetailModal}
                    onHide={() => setShowDetailModal(false)}
                    size="xl"
                    centered
                >
                    <Modal.Header closeButton className="px-4 py-3 border-bottom">
                        <Modal.Title className="fs-5 fw-bold text-dark">
                            Product Detail ({selectedProduct.product_code})
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        <Row className="g-4">
                            {/* Product Gallery Section */}
                            <Col md={5} className="text-center">
                                <div className="border rounded p-3 mb-3 bg-white d-flex align-items-center justify-content-center" style={{ minHeight: "280px" }}>
                                    <img
                                        src={
                                            activeImage
                                                ? `${process.env.REACT_APP_IMG_URL}${activeImage}`
                                                : "/assets/images/placeholder.png"
                                        }
                                        alt={selectedProduct.title}
                                        className="img-fluid rounded"
                                        style={{ maxHeight: "300px", objectFit: "contain" }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "https://via.placeholder.com/300?text=No+Image";
                                        }}
                                    />
                                </div>

                                {selectedProduct.images && selectedProduct.images.length > 0 && (
                                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                                        {selectedProduct.images.map((imgObj) => (
                                            <div
                                                key={imgObj.id}
                                                onClick={() => setActiveImage(imgObj.image)}
                                                className={`border rounded p-1 style-pointer ${activeImage === imgObj.image ? "border-primary border-2" : ""}`}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <img
                                                    src={`${process.env.REACT_APP_IMG_URL}${imgObj.image}`}
                                                    alt="Gallery"
                                                    style={{ width: "50px", height: "50px", objectFit: "cover" }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = "https://via.placeholder.com/50?text=Img";
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Col>

                            {/* Details Section */}
                            <Col md={7}>
                                <h4 className="mb-2 fw-bold text-dark">{selectedProduct.title}</h4>
                                <p className="text-muted mb-3">Product Code: <span className="fw-semibold text-primary">{selectedProduct.product_code}</span></p>

                                <Card className="p-3 mb-3 bg-light border-0">
                                    <Row className="g-3">
                                        <Col sm={6}>
                                            <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>Category</small>
                                            <span className="fs-6 fw-bold text-dark">{selectedProduct.category?.name || "N/A"}</span>
                                        </Col>
                                        <Col sm={6}>
                                            <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>Brand</small>
                                            <span className="fs-6 fw-bold text-dark">{selectedProduct.brand?.name || "N/A"}</span>
                                        </Col>
                                        <Col sm={6}>
                                            <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>Product Condition</small>
                                            <Badge bg="secondary" className="px-2 py-1 text-capitalize">{selectedProduct.product_condition}</Badge>
                                        </Col>
                                        <Col sm={6}>
                                            <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>Selling Style</small>
                                            <Badge bg={selectedProduct.selling_style === "Fixed" ? "info" : "warning"} className="px-2 py-1">{selectedProduct.selling_style}</Badge>
                                        </Col>
                                        <Col sm={6}>
                                            <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>Buying Price Dollar</small>
                                            <span className="fs-5 fw-bold text-success">${Number(selectedProduct.sell_price_dollar).toLocaleString()}</span>
                                        </Col>
                                        <Col sm={6}>
                                            <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>Buying Price</small>
                                            <span className="fs-6 fw-bold text-dark">{Number(selectedProduct.buying_price).toLocaleString()}</span>
                                        </Col>
                                        <Col sm={6}>
                                            <small className="text-muted d-block fw-semibold text-uppercase" style={{ fontSize: "11px" }}>Product Quantity</small>
                                            <span className="fs-6 fw-bold text-dark">{selectedProduct.product_qty}</span>
                                        </Col>
                                    </Row>
                                </Card>

                                {selectedProduct.short_description && (
                                    <div className="mb-3">
                                        <h6 className="fw-bold text-secondary mb-1">Short Description</h6>
                                        <div className="text-muted small" dangerouslySetInnerHTML={{ __html: selectedProduct.short_description }} />
                                    </div>
                                )}
                            </Col>

                            {/* Full Description */}
                            {selectedProduct.description && (
                                <Col md={12}>
                                    <hr className="my-2" />
                                    <h6 className="fw-bold text-secondary mb-2">Description</h6>
                                    <div
                                        className="border rounded p-3 bg-light text-muted small"
                                        dangerouslySetInnerHTML={{ __html: selectedProduct.description }}
                                    />
                                </Col>
                            )}
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="px-4 py-2 border-top">
                        <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </PageLayout>
    );
}